import { Injectable } from "@angular/core";
import { ToastrService } from "ngx-toastr";

import { HttpRequestService } from "app/auth/service/http-request.service";
import { AuthenticationService } from "./authentication.service";
import { convertToUUID } from "../helpers/binaryData";
import { CustomerService } from "./customer.service";
import { HttpClient } from "@angular/common/http";
import { environment } from "environments/environment";
import { Observable } from "rxjs";
import { CoreConfigService } from "@core/services/config.service";

@Injectable({ providedIn: "root" })
export class ThemeConfigService {
  public convertToUUID = convertToUUID;
  public defaultColors = {
    primary: "#24bdd7",
    secondary: "#f27922",
    hoverprimary: "#198395",
    hoversecondary: "#d5600d",
    info: "#3264DC",
    warning: "#ffaa32",
    light: "#f6f6f6",
    dark: "#c0c0c0",
    custom: "#00ff00",
    success: "#37C846",
    inputDarkMode: "#C6C8CE",
    colorDarkMode: "#283046",
  };

  public url = `${environment.apiUrl}/private/themes`;

  constructor(
    private _http: HttpClient,
    private _auth: AuthenticationService,
    private readonly _coreConfigService: CoreConfigService
  ) {}

  create = (data): Observable<any> =>
    this._http.post(`${this.url}/create`, data);

  evaluateTheme = (host): Observable<any> =>
    this._http.post(`${environment.apiUrl}/public/external/theme`, host);

  public setThemeFromConfig(config) {
    if (!config) {
      console.warn('No config provided to setThemeFromConfig');
      return;
    }
    
    console.log('Setting theme from config:', config);
    
    const root: any = document.querySelector(":root");
    if (!root) {
      console.error('Root element not found');
      return;
    }

    this.setColors(root, config);

    if (config.logo) {
      this.setLogo(config.logo);
    }
    
    if (config.loading_logo) {
      this.getLoadingImage(config.loading_logo);
    }
  }

  private setColors(root: any, config: any) {
    const colors = this.extractColors(config);
    
    // console.log('Extracted colors:', colors);

    const validatedColors = this.validateColors(colors);

    const colorVariants = this.generateColorVariants(validatedColors);
    
    this.applyColorVariables(root, validatedColors, colorVariants);
  }

  private extractColors(config: any) {
    return {
      primary: config.primary_color || config.primary || this.defaultColors.primary,
      secondary: config.secondary_color || config.secondary || this.defaultColors.secondary,
      hoverprimary: config.hoverprimary_color || config.hoverprimary || this.defaultColors.hoverprimary,
      hoversecondary: config.hoversecondary_color || config.hoversecondary || this.defaultColors.hoversecondary,
      info: config.info_color || config.info || this.defaultColors.info,
      warning: config.warning_color || config.warning || this.defaultColors.warning,
      success: config.success_color || config.success || this.defaultColors.success
    };
  }

  private validateColors(colors: any) {
    const validated: { [key: string]: string } = {};
    for (const [key, color] of Object.entries(colors)) {
      if (this.isValidHexColor(color as string)) {
        validated[key] = color as string;
      } else {
        console.warn(`Invalid color for ${key}: ${color}, using default`);
        validated[key] = this.defaultColors[key] || '#000000';
      }
    }
    return validated;
  }

  private isValidHexColor(color: string): boolean {
    return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
  }

  private generateColorVariants(colors: any) {
    const variants: { [key: string]: any } = {};
    
    for (const [key, color] of Object.entries(colors)) {
      const rgb = this.hexToRgb(color as string);
      if (rgb) {
        variants[key] = {
          rgb: `${rgb.r} ${rgb.g} ${rgb.b}`,
          dark: this.generateDarkVariant(color as string)
        };
      }
    }
    
    return variants;
  }

  private generateDarkVariant(color: string): string {
    return color.substring(0, 7) + '22';
  }

  private applyColorVariables(root: any, colors: any, variants: any) {
    root.style.setProperty("--primary", colors.primary);
    root.style.setProperty("--secondary", colors.secondary);
    root.style.setProperty("--hoverprimary", colors.hoverprimary);
    root.style.setProperty("--hoversecondary", colors.hoversecondary);
    root.style.setProperty("--info", colors.info);
    root.style.setProperty("--warning", colors.warning);
    root.style.setProperty("--success", colors.success);

    if (variants.primary) {
      root.style.setProperty("--rgbprimary", variants.primary.rgb);
      root.style.setProperty("--darkprimary", variants.primary.dark);
    }
    if (variants.secondary) {
      root.style.setProperty("--rgbsecondary", variants.secondary.rgb);
      root.style.setProperty("--darksecondary", variants.secondary.dark);
    }

    console.log('Applied color variables to root element');
  }

  setCompanyConfig = (config) => {
    if (config.hostname === "default") {
      this.setThemeFromConfig(this.defaultColors);
    } else {
      this.setThemeFromConfig(config);
    }
  };

  public getLoadingImage(loading_logo) {
    if (!loading_logo) return;
    
    try {
      const favIcon: HTMLLinkElement = document.querySelector("#appIcon");
      if (favIcon) {
        favIcon.href = loading_logo;
      }
      
      const root: any = document.querySelector(":root");
      if (root) {
        const logo = `url('${loading_logo}')`;
        root.style.setProperty("--loading-logo", logo);
      }
    } catch (error) {
      console.error('Error setting loading image:', error);
    }
  }

  public setLogo(logo) {
    if (!logo) return;
    
    try {
      this._coreConfigService.setConfig(
        { app: { appLogoImage: logo } },
        { emitEvent: true }
      );
    } catch (error) {
      console.error('Error setting logo:', error);
    }
  }

  getAll = (): Observable<any> => this._http.get(`${this.url}/list`);

  getById = (id): Observable<any> => this._http.get(`${this.url}/${id}`);

  getByCompany = (company): Observable<any> =>
    this._http.get(`${this.url}/company-config/${company}`);

  update = (data, id): Observable<any> =>
    this._http.put(`${this.url}/${id}`, data);

  saveFile(formData: FormData, company): Observable<any> {
    const headers = new Headers();
    headers.append("Content-Type", "multipart/form-data");
    headers.append("Accept", "application/json");
    return this._http.post(`${this.url}/upload-logo/${company}`, formData);
  }

  hexToRgb = (hex) => {
    if (!hex) return null;
    
    var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : null;
  };

  setThemeConfig = (configId) => {
    if (configId) {
      this.getById(configId).subscribe(
        (config) => {
          console.log("CONFIG id theme", config);
          this.setThemeFromConfig(config);
        },
        (err: any) => console.error("error al theme_config", err)
      );
    } else {
      this.setThemeFromConfig(this.defaultColors);
    }
  };

  defaultConfig(root, companyConfig) {
    this.setThemeFromConfig(companyConfig);
  }

  get companyId() {
    return this._auth.companyId;
  }

  get userId() {
    return this._auth.userId;
  }
}
